import React, { useState, useEffect } from 'react';
import { Calendar, Clock, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { $api } from '../../services';
import { useNavigate, useParams } from 'react-router-dom';
import decryptData from '../decryptData';
import { toast } from 'react-toastify';

type TimeSlot = {
  start: string;
  end: string;
};

type WeekDayTimeSlots = {
  [key: string]: TimeSlot[];
};

type ScheduleType = 'daily' | 'weekly' | 'custom';

interface Schedule {
  type: ScheduleType;
  selectedDates?: string[];
  selectedWeekDays?: string[];
  timeSlots: WeekDayTimeSlots;
}

interface UseCase {
  kycType: string;
  description: string;
  schedule: Schedule;
}

interface UseCases {
  [key: string]: UseCase;
}

interface KYCScheduleViewerProps {
  formData: UseCase;
}

interface AppointmentData {
  date: string;
  timeSlot: {
    start: string;
    end: string;
  };
  timestamp: number;
}

const formatTime = (time24: string): string => {
  const [hours, minutes] = time24.split(':');
  const hour = parseInt(hours, 10);
  const period = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${period}`;
};

const generateTimesForSlot = (date: Date, start: string, end: string): string[] => {
  const times = [];
  let currentTime = new Date(`2000-01-01T${start}`);
  const endTime = new Date(`2000-01-01T${end}`);
  
  // Check if selected date is today
  const isToday = new Date().toDateString() === date.toDateString();
  const now = new Date();
  
  while (currentTime < endTime) {
    const timeString = currentTime.toTimeString().slice(0, 5);
    
    // If it's today, only include future times (adding a small buffer)
    if (!isToday || (isToday && `${timeString}:00` > now.toTimeString())) {
      times.push(timeString);
    }
    
    currentTime.setMinutes(currentTime.getMinutes() + 15);
  }
  
  return times;
};

const KYCScheduleViewer = ({ schedule, available_at } : { schedule: any, available_at: string }) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [selectedTime, setSelectedTime] = useState<string | null>(null);

  const getDaysInMonth = (date: Date): number => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date): number => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-CA');
  };

  const isDateInPast = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    return ((date < tomorrow) || (date < new Date(available_at)));
  };

  const isDateAvailable = (date: Date): boolean => {
    if (isDateInPast(date)) return false;

    const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'long' });
    const dateString = formatDate(date);
    const scheduleType = schedule?.type || 'daily';

    switch (scheduleType) {
      case 'daily':
        return true;
      case 'weekly':
        return schedule?.selectedWeekDays?.includes(dayOfWeek) || false;
      case 'custom':
        return schedule?.selectedDates?.includes(dateString) || false;
      default:
        return false;
    }
  };

  const isPastMonth = (date: Date): boolean => {
    const today = new Date();
    return date.getFullYear() < today.getFullYear() ||
      (date.getFullYear() === today.getFullYear() && date.getMonth() < today.getMonth());
  };

  const getTimeSlots = (date: Date): TimeSlot[] => {
    const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'long' });
    return schedule?.timeSlots?.[dayOfWeek] || [];
  };

  const changeMonth = (offset: number): void => {
    const newDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + offset, 1);
    if (!isPastMonth(newDate)) {
      setCurrentMonth(newDate);
    }
  };

  const handleDateClick = (day: number): void => {
    const clickedDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    if (isDateAvailable(clickedDate)) {
      setSelectedDate(clickedDate);
      setSelectedTime(null);
    }
  };

  const [ creatingAppointment, setCreatingAppointment ] = useState(false);

    let { slug } = useParams();
    let slugArray = slug?.split("-");
    const slugLength = slugArray?.length
    const unit_id = ( slugArray && slugLength ) ? slugArray[slugLength-1] : ''

    const tenant_id = decryptData(localStorage.getItem('liveableUserId') || '', import.meta.env.VITE_APP_DECRYPT_USER_ID_SECRET_KEY)
  const router = useNavigate();
  const handleAppointmentSubmit = async() => {
    const pad = (num: any) => String(num).padStart(2, '0');
    if (!selectedDate || !selectedTime) return;
    try{
        setCreatingAppointment(true)
        const response = await $api.post('api-property-listing/v1/booking/submit', {
            unit_id,
            tenant_id,
            date: selectedDate.getFullYear() + "-" + pad(selectedDate.getMonth() + 1) + "-" + pad(selectedDate.getDate()),
            time: selectedTime,
        })
        if($api.isSuccessful(response)){
          router(`/booking-confirmed/${response?.data?.data?.id}`)
          toast.success('Booking created successfully')
        }
    }
    catch(err){
        console.log(err)
    }
    finally{
        setCreatingAppointment(false)
    }
  }

  useEffect(() => {
    setSelectedDate(null);
    setSelectedTime(null);
  }, [schedule]);

  return (
    <div className="w-full max-w-[400px] mx-auto p-4 bg-white rounded-lg shadow">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <button 
            onClick={() => changeMonth(-1)} 
            disabled={isPastMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}
            className={`p-2 rounded transition-colors ${
              isPastMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))
                ? 'text-gray-300 cursor-not-allowed'
                : 'hover:bg-gray-100'
            }`}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="font-medium">
            {currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
          </span>
          <button onClick={() => changeMonth(1)} className="p-2 hover:bg-gray-100 rounded">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="text-center text-sm font-medium text-gray-500">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: getFirstDayOfMonth(currentMonth) }).map((_, i) => (
            <div key={`empty-${i}`} />
          ))}
          {Array.from({ length: getDaysInMonth(currentMonth) }).map((_, i) => {
            const day = i + 1;
            const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
            const isAvailable = isDateAvailable(date);
            const isPast = isDateInPast(date);
            const isSelected = selectedDate && 
              selectedDate.getDate() === day && 
              selectedDate.getMonth() === currentMonth.getMonth();

            return (
              <button
                key={day}
                onClick={() => handleDateClick(day)}
                disabled={!isAvailable || isPast}
                className={`p-2 text-center rounded-lg flex items-center justify-center transition-colors
                  ${isPast 
                    ? 'text-gray-300 cursor-not-allowed'
                    : isAvailable 
                      ? isSelected
                        ? 'bg-blue-900 text-white'
                        : 'hover:bg-gray-100'
                      : 'text-gray-300 cursor-not-allowed'
                  }`}
              >
                {day}
              </button>
            );
          })}
        </div>
      </div>

      {selectedDate && (
        <div className="mt-6">
          <p className="text-lg font-medium mb-4">Select Preferred Time</p>
          <select
            value={selectedTime || ''}
            onChange={(e) => setSelectedTime(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
          >
            <option value="">Select time</option>
            {getTimeSlots(selectedDate).map((slot, slotIndex) => {
              const times = generateTimesForSlot(selectedDate, slot.start, slot.end);
              // Only show slots that have available times
              if (times.length === 0) return null;
              
              return (
                <optgroup 
                  key={slotIndex} 
                  label={`${formatTime(slot.start)} - ${formatTime(slot.end)}`}
                >
                  {times.map((time) => (
                    <option key={time} value={time}>
                      {formatTime(time)}
                    </option>
                  ))}
                </optgroup>
              );
            })}
          </select>
        </div>
      )}

      {selectedDate && selectedTime && (
        <button
          onClick={handleAppointmentSubmit}
          className="w-full mt-6 bg-blue-900 text-white py-3 rounded-lg hover:bg-blue-800 transition-colors"
        >
            {
                creatingAppointment
                    ?
                    <div className='loading'></div>
                    :
                    "Confirm Appointment"
            }
        </button>
      )}
    </div>
  );
};

const ScheduleViewer = ({ schedule, available_at } : { schedule: any, available_at: string }) => {
  return (
    <div className="p-4">
      <KYCScheduleViewer schedule={schedule} available_at={available_at} />
    </div>
  );
};

export default ScheduleViewer